import { useRef, type ReactNode } from 'react';
import { useDispatch } from 'react-redux';

import { useScope } from '@/hooks/use-scope';
import { tenantApi, updateGlobalScope } from '@/redux/api/tenant-api';

/**
 * Provider that syncs URL-based scope to RTK Query baseQuery
 *
 * Extracts tenant and organization slugs from URL params using useScope()
 * and updates the global scope storage that RTK Query baseQuery reads from.
 *
 * This approach is necessary because RTK Query's prepareHeaders cannot
 * use React hooks directly.
 *
 * On scope change (after the first render), also resets the RTK Query
 * cache. The org slug is sent as a request header rather than a query arg,
 * so it is invisible to RTK's cache key — without a reset, switching orgs
 * would serve stale data from the previous org.
 *
 * @example
 * ```tsx
 * <ReduxProvider>
 *   <ScopeHeadersProvider>
 *     {children}
 *   </ScopeHeadersProvider>
 * </ReduxProvider>
 * ```
 */
export function ScopeHeadersProvider({ children }: { children: ReactNode }) {
  const { tenantSlug, organizationSlug } = useScope();
  const dispatch = useDispatch();
  const prevScopeRef = useRef<string | null>(null);

  // CRITICAL: Update scope SYNCHRONOUSLY during render, not in useEffect
  // This ensures RTK Query has the correct scope before any API calls are made
  const scopeKey = `${tenantSlug}:${organizationSlug}`;
  if (prevScopeRef.current !== scopeKey) {
    const isFirstRun = prevScopeRef.current === null;
    prevScopeRef.current = scopeKey;
    updateGlobalScope(tenantSlug, organizationSlug);
    if (!isFirstRun) {
      dispatch(tenantApi.util.resetApiState());
    }
  }

  return <>{children}</>;
}
